
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Paper, Typography, styled } from '@mui/material';

import { ConditionMode, IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import {
    CollaboratingGroupContext,
    CrfCondition,
    Form,
    FormSubmitButton,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    IFormValidationError,
    IPatientSummaryUpdateListener,
    InstitutionContext,
    MasterGroupContext,
    OnlinePatientManagementContext,
    PageLayout,
    PatientBreadcrumbs,
    PatientContext,
    PatientInformation,
    PatientStateContext,
    PatientSummary,
    PatientSummaryManager,
    ResponseStatus,
    RouteLoading,
    Select,
    Text,
    TreatmentContext,
    ValidationResultType,
    useAsyncAction,
    usePatientSave,
    useSnackbar
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { DateTime } from 'luxon';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Dtos from '../api/dtos';
import { AppSettings } from '../AppSettings';
import RadiologistSelector from '../components/forms/fields/RadiologistSelector';
import PatientStatusInformation from '../components/page/patientSummary/PatientStatusInformation';
import PatientSummaryAlerts from '../components/page/patientSummary/PatientSummaryAlerts';
import PatientSummaryAllocationInformation from '../components/page/patientSummary/PatientSummaryAllocationInformation';
import PatientActiveDirectory from '../components/PatientActiveDirectory';
import usePatientAllocationFactors from '../hooks/data/usePatientAllocationFactors';
import usePatientSummaryEventActions from '../hooks/page/patientSummary/usePatientSummaryEventActions';

interface IMarkAsIneligibleForm {
    ineligibleReason?: number,
    ineligibleReasonOtherSpecify?: string,
    ineligibleBreastCancerSubtype?: number
}


const StyledContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3)
}))

const StyledTitle = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
    gap: '1rem'
}))

const StyledButton = styled(Button)(() => ({
    height: "fit-content",
}))

const StyledButtonGroup = styled(Box)(({ theme }) => ({
    margin: theme.spacing(2, 0, 0, 0),
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    display: 'flex'
}))

const StyledDialogTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3),
    fontSize: '1.25rem',
    fontWeight: 500
}))

const INELIGIBILITY_REASONS = [
    'Participant declined trial because they prefer standard of care',
    'Participant declined no reason given or not interested in the trial',
    'Treating clinician/MDT recommended radiation therapy',
    'Ineligible breast cancer sub type',
    'Ineligible for 10 year follow up',
    'Previous breast cancer',
    'Planned mastectomy for index lesion',
    'Known BC gene mutation carrier',
    'Contraindication to breast MRI scanning',
    'Additional lesions identified at MRI unable to be biopsied',
    'Has moderate or marked BPE in the breast containing the index cancer (where MRI done prior to registration)',
    'Surgery >8 weeks after MRI',
    'Unable to allocate participant >8 weeks after last surgery',
    'Other'
]


const INELIGIBLE_BREAST_CANCER_SUBTYPE = [
    'ER and HER2 negative; or triple negative breast cancer',
    'Lymphovascular invasion reported on diagnostic biopsy',
    'Multifocal/multicentric breast cancer on breast imaging prior to registration',
    'Bilateral breast cancer',
    'Distant metastasis at diagnosis'
]

const ineligibleBreastCancerSubType = ({ formState }: ICrfConditionParameters<IMarkAsIneligibleForm>) => formState?.value?.ineligibleReason === Dtos.LookupIneligibleReasonType.IneligibleBreastCancerSubType;
const ineligibleReasonOtherSpecify = ({ formState }: ICrfConditionParameters<IMarkAsIneligibleForm>) => formState?.value?.ineligibleReason === Dtos.LookupIneligibleReasonType.OtherSpecify;

const PatientSummaryPage: FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { routeParameters } = useContext(OnlinePatientManagementContext);

    const routePatientStudyNumber = params[routeParameters.patientStudyNumber] ?? undefined;
    const routeInstitutionCode = params[routeParameters.institutionCode] ?? undefined;

    const { enqueueSnackbar } = useSnackbar();

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution, loading: institutionLoading } = useContext(InstitutionContext);
    const { data: patient, loading: patientLoading, mutate: patientMutate, refresh: patientRefresh } = useContext(PatientContext);
    const { serviceStackClient } = useContext(OnlinePatientManagementContext);

    const { action: savePatient, pending: patientSaving } = useAsyncAction(
        usePatientSave(patientMutate), 
        useMemo(() => ({ 
            errorAccessor: (err) => err?.responseStatus as ResponseStatus
        }), [])
    );

    const [patientValid, setPatientValid] = useState(false);
    const [patientCritical, setPatientCritical] = useState(false); 

    const managerRef = useRef<PatientSummaryManager>(undefined);

    const onPatientValidChange: IPatientSummaryUpdateListener = useCallback((summaryState) => {
        setPatientValid(!!summaryState?.valid);
        setPatientCritical((summaryState?.data?.result ?? ValidationResultType.Critical) === ValidationResultType.Critical)
    }, [setPatientValid, setPatientCritical,])

    const saving = patientSaving;

    const updatePatientState = useCallback(async (patientStateId: Dtos.PatientStateType) => {
        await savePatient(new Dtos.Patient({ ...patient, previousPatientStateId: patient?.patientStateId, patientStateId: patientStateId }), false);

        if (managerRef.current) {
            await managerRef.current.load();
        }
        
    }, [patient, savePatient, managerRef]);

    const [systemErrorDialogOpen, setSystemErrorDialogOpen] = useState<boolean>(false);

    const onCompleteConsentClick = useCallback(async () => {
        if(!patientValid)
        {
            setSystemErrorDialogOpen(true);
            return;
        }

        try {
            await updatePatientState(Dtos.PatientStateType.Registration);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Completed
                    </AlertTitle>
                    The participant consent was successfully completed.
                </>,
                { variant: 'success' }
            );
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Not Completed
                    </AlertTitle>
                    An error occurred while attempting to complete the consent.
                </>,
                { variant: 'critical' }
            );
        }
    }, [updatePatientState, enqueueSnackbar, patientValid, setSystemErrorDialogOpen, navigate, patient, institution, patientRefresh]);

    useEffect(() => {
        //redirect if route patient/institution doesn't match loaded patient/institution
        //this is most probably going to happen once a patient goes from having a temporary study number to a permanent one
        //i.e. the consent has been completed

        //Also check that the patient and institution actually exist
        if((!patientLoading && !institutionLoading) && (!patient?.studyNumber || !institution?.code)) {
            navigate("/registration/", { replace: true });
        }
        else if (patient?.studyNumber != routePatientStudyNumber || institution?.code != routeInstitutionCode) {
            navigate(`/registration/${institution?.code}/${patient?.studyNumber}`, 
                { replace: true });
        }
    }, [patient?.studyNumber, routePatientStudyNumber, routeInstitutionCode, institution?.code, navigate, patientLoading, institutionLoading])

    const onAllocateClick = useCallback(async () => {
        if(!patientValid)
        {
            setSystemErrorDialogOpen(true);
            return;
        }

        try {
            await updatePatientState(Dtos.PatientStateType.Allocated);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Particpant Allocated
                    </AlertTitle>
                    The participant was succcessfully allocated.
                </>,
                { variant: 'success' }
            );
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Particpant Not Allocated
                    </AlertTitle>
                    An error occurred while attempting to allocate the participant.
                </>,
                { variant: 'critical' }
            );
        }
    }, [updatePatientState, enqueueSnackbar, patientValid, setSystemErrorDialogOpen]);

    const onCompleteRadiologyReviewClick = useCallback(async () => {
        if(!patientValid)
        {
            setSystemErrorDialogOpen(true);
            return;
        }

        try {
            await updatePatientState(Dtos.PatientStateType.Allocation);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Radiology Review Completed
                    </AlertTitle>
                    The radiology review was succcessfully completed.
                </>,
                { variant: 'success' }
            );
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Radiology Review Not Completed
                    </AlertTitle>
                    An error occurred while attempting to complete the radiology review.
                </>,
                { variant: 'critical' }
            );
        }
    }, [updatePatientState, enqueueSnackbar, patientValid, setSystemErrorDialogOpen]);

    // const onIneligibleClick = useCallback(async () => {
    //     try {
    //         await updatePatientState(Dtos.PatientStateType.Ineligible);

    //         enqueueSnackbar(
    //             <>
    //                 <AlertTitle>
    //                     Participant Marked As Ineligible
    //                 </AlertTitle>
    //                 The participant was successfully marked ineligible.
    //             </>,
    //             { variant: 'success' }
    //         );
    //     }
    //     catch {
    //         enqueueSnackbar(
    //             <>
    //                 <AlertTitle>
    //                     Participant Not Marked As Ineligible
    //                 </AlertTitle>
    //                 An error occurred while attempting to mark the participant as ineligible.
    //             </>,
    //             { variant: 'critical' }
    //         );
    //     }
    // }, [updatePatientState, enqueueSnackbar]);

    const onRestoreClick = useCallback(async () => {
        try {
            await updatePatientState((patient as Dtos.Patient)?.previousPatientStateId ?? Dtos.PatientStateType.Consent);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Restored
                    </AlertTitle>
                    The participant was successfully restored.
                </>,
                { variant: 'success' }
            );
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Restored
                    </AlertTitle>
                    An error occurred while attempting to restore the participant.
                </>,
                { variant: 'critical' }
            );
        }
    }, [updatePatientState, enqueueSnackbar, patient]);

    const {
        permissions: [
            canImpersonatePatient,
            canManagePatientAccount,
            canAdministerOpms,
            canUpdatePatient,
            canRadiologyReportReview,
            canCrfUpdate,
            canBypassErrors
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsPatientImpersonate,
            Permission.OpmsPatientAccountManagement,
            Permission.OpmsAdminister,
            Permission.OpmsPatientUpdate,
            Permission.RadiologyReviewAccess,
            Permission.EcrfView,
            Permission.OpmsBypassErrors
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const { data: patientState } = useContext(PatientStateContext);
    const { data: treatment } = useContext(TreatmentContext);

    const patientCaption = useMemo(() => !canAdministerOpms ? (patient as Dtos.Patient)?.initials ?? 'Unknown' : ' ', [patient, canAdministerOpms]);
    const patientStateCaption = useMemo(() => DateTime.fromISO((patient as Dtos.Patient)?.patientStateChangeDate ?? DateTime.local().toISO({ includeOffset: false })).toFormat('dd/MM/yyyy'), [patient]);
    const patientStateName = useMemo(() => {
        var patientStateName = patientState?.name;
        
        if (patient?.patientStateId === Dtos.PatientStateType.Allocated && treatment?.name) {
            patientStateName = `${patientStateName} - ${treatment?.name}`
        }

        var withdrawalState = (patient as Dtos.Patient)?.withdrawalStateId === Dtos.WithdrawalStateType.FullyWithdrawn ? 'Withdrawn (Follow Up)\nWithdrawn (PROMs)' :
            (patient as Dtos.Patient)?.withdrawalStateId === Dtos.WithdrawalStateType.WithdrawnFollowUp ? 'Withdrawn (Follow Up)' :
            (patient as Dtos.Patient)?.withdrawalStateId === Dtos.WithdrawalStateType.WithdrawnProms ? 'Withdrawn (PROMs)' : null;

        if (withdrawalState) {
            return `${patientStateName}\n${withdrawalState}`;
        }

        return patientStateName;
    }, [patient?.patientStateId, patientState, treatment])

    const eventActionMapping = usePatientSummaryEventActions(
        saving, 
        updatePatientState, 
        enqueueSnackbar, 
        serviceStackClient, 
        patient, 
        patientValid, 
        institution, 
        canUpdatePatient,
        managerRef.current
    )

    const [patientScreeningForm, setPatientScreeningForm] = useState<Dtos.ScreeningForm|null>(null);
    const [screenFailDialogOpen, setScreenFailDialogOpen] = useState<boolean>(false);
    const [screenFailSaving, setScreenFailSaving] = useState<boolean>(false);

    useEffect(() => {
        const effectAction = async () => {
            try {
                const response = await serviceStackClient.get(new Dtos.ScreeningFormGetSingleByCodes({
                    patientStudyNumber: patient?.studyNumber,
                    eventDefinitionCode: 'screening',
                    eventRepeat: 1,
                    formRepeat: 1,
                    institutionCode: institution?.code
                }));

                setPatientScreeningForm(response.form);
            }
            catch {

            }
        };
        
        effectAction();
    }, [serviceStackClient, patient?.studyNumber, institution, setPatientScreeningForm]);

    const onScreenFailSubmit: IFormSubmit<Dtos.ScreeningForm, Dtos.IFormValidationError> = useCallback(async (form) => {
        setScreenFailSaving(true);

        const screeningForm = Object.assign({}, patientScreeningForm as Dtos.ScreeningForm);

        screeningForm.outcome = Dtos.LookupScreeningOutcomeType.Fail;
        screeningForm.screenFailReason = form.value?.screenFailReason;

        let refreshPatient = false;

        try {
            await serviceStackClient.post(new Dtos.ScreeningFormPostSave({
                form: screeningForm
            }));

            refreshPatient = true;

            setScreenFailSaving(false);

            setScreenFailDialogOpen(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Marked As Screen Fail
                    </AlertTitle>
                        The participant was successfully marked as screen fail.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setScreenFailSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Marked As Screen Fail
                    </AlertTitle>
                    {error?.responseStatus?.errorCode === 'ClientViewableException' ? error.responseStatus.message : 'An error occurred while attempting to mark the participant as screen fail.'}
                </>,
                { variant: 'critical' }
            );
        }
    

        if (refreshPatient) {
            await patientRefresh();

            if (managerRef.current) {
                await managerRef.current.load();
            }
        }
    }, [serviceStackClient, enqueueSnackbar, updatePatientState, setScreenFailDialogOpen, patientScreeningForm, patientRefresh, patient, setScreenFailSaving, managerRef]);

    const screenFailValidate: IFormValidate<Dtos.ScreeningForm, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.screenFailReason === undefined) {
            errors['screenFailReason'] = [
                {
                    property: 'screenFailReason',
                    code: 'SF-006',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Critical
                }
            ];
        }

        return errors;
    }, [institution]);

    const screeningFormLookups: Dtos.ILookup[] = [
        {
            propertyName: 'screenFailReason',
            items: [
                {
                    id: Dtos.LookupScreenFailReasonType.ParticipantIneligible,
                    value: 'Participant ineligible for registration test'
                },
                {
                    id: Dtos.LookupScreenFailReasonType.ClinicianDeemedUnsuitable,
                    value: 'Clinician/MDT deemed unsuitable'
                },
                {
                    id: Dtos.LookupScreenFailReasonType.ParticipantPrefersStandardCare,
                    value: 'Participant declined trial because they prefer standard of care'
                },
                {
                    id: Dtos.LookupScreenFailReasonType.ParticipantDeclinedNoReason,
                    value: 'Participant declined no reason given or not interested in the trial'
                }
            ]
        }
    ];

    const formsDueScheduleReportUrl = useMemo(() => {
        const uri = AppSettings.reportServerUrl;
        const path = `${AppSettings.reportServerDownloadPath}?/${AppSettings.reportServerFolderPath}`;
        const report = AppSettings.reportServerFormsDueSchedule

        return `${uri}/${path}/${report}&rs:ClearSession=true&rs:Format=PDF&userkey=EmailLink&randno=${patient?.studyNumber}`;
    }, [patient?.studyNumber]);
    
    useEffect(() => {
        if(patient?.patientStateId === Dtos.PatientStateType.Screening) {
            navigate(`/registration`);
        }
    }, [patient?.patientStateId, navigate]);

    const [reviewStartDialogOpen, setReviewStartDialogOpen] = useState<boolean>(false);
    const [reviewStartSaving, setReviewStartSaving] = useState<boolean>(false);

    const onRegisterClick = useCallback(() => {
        if(!patientValid)
        {
            setSystemErrorDialogOpen(true);
            return;
        }

        setReviewStartDialogOpen(true);
    }, [patientValid, setSystemErrorDialogOpen, setReviewStartDialogOpen])

    const reviewStartValidate: IFormValidate<Dtos.RadiologyReport, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.radiologist == null) {
            errors['radiologist'] = [
                {
                    property: 'radiologist',
                    code: 'R-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Normal
                }
            ];
        }
        return errors;
    }, []);

    const onReviewStartSubmit: IFormSubmit<Dtos.RadiologyReport, IFormValidationError> = useCallback(async (form) => {
        setReviewStartSaving(true);

        let refreshPatient = false;

        try {
            await serviceStackClient.post(new Dtos.StartReview({
                patientId: patient?.id,
                radiologist: form.value?.radiologist
            }));

            refreshPatient = true;

            setReviewStartSaving(false);

            setReviewStartDialogOpen(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Registration Completed
                    </AlertTitle>
                    Registration completed successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setReviewStartSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Radiologist Review Not Started
                    </AlertTitle>
                    {error?.responseStatus?.errorCode === 'ClientViewableException' ? error?.responseStatus.message : 'An error occurred while attempting to start the radiologist review.'}
                </>,
                { variant: 'critical' }
            );
        }
    

        if (refreshPatient) {
            await patientRefresh();

            if (managerRef.current) {
                await managerRef.current.load();
            }
        }
    
    }, [serviceStackClient, enqueueSnackbar, updatePatientState, setReviewStartDialogOpen, setReviewStartSaving, patientRefresh, patient, managerRef]);

    const isRadiologist = canRadiologyReportReview && !canCrfUpdate;


    const [correctionRequiredDialogOpen, setCorrectionRequiredDialogOpen] = useState<boolean>(false);
    const [patientStatusSaving, setPatientStatusSaving] = useState<boolean>(false);

    const correctionRequiredInitial = useMemo(() => {
        return {
            patientId: patient?.id,
            patientStateId: Dtos.PatientStateType.CorrectionRequired
        } as Dtos.SetPatientStatus
    }, [patient]);

    const patientStatusValidate: IFormValidate<Dtos.SetPatientStatus, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.patientStateId == null) {
            errors['patientStateId'] = [
                {
                    property: 'notes',
                    code: 'S-000',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Normal
                }
            ];
        }

        if (formState.value?.notes == null) {
            errors['notes'] = [
                {
                    property: 'notes',
                    code: 'S-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Normal
                }
            ];
        }
        return errors;
    }, []);

    const onPatientStatusSubmit: IFormSubmit<Dtos.SetPatientStatus, IFormValidationError> = useCallback(async (form) => {
        setPatientStatusSaving(true);

        let refreshPatient = false;

        try {
            await serviceStackClient.post(new Dtos.SetPatientStatus({
                patientId: form.value?.patientId,
                patientStateId: form.value?.patientStateId,
                notes: form.value?.notes
            }));

            refreshPatient = true;

            setPatientStatusSaving(false);

            setCorrectionRequiredDialogOpen(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                    Participant Status Updated
                    </AlertTitle>
                    Participant status was successfully updated.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setPatientStatusSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Status Not Updated
                    </AlertTitle>
                    {error.responseStatus?.errorCode === 'ClientViewableException' ? error.responseStatus.message : 'An error occurred while attempting to update the participant status.'}
                </>,
                { variant: 'critical' }
            );
        }
    

        if (refreshPatient) {
            await patientRefresh();

            if (managerRef.current) {
                await managerRef.current.load();
            }
        }
    
    }, [serviceStackClient, enqueueSnackbar, setCorrectionRequiredDialogOpen, setPatientStatusSaving, patientRefresh, managerRef]);


    useEffect(() => {
        if(isRadiologist) 
        {
            navigate(`/registration`)
        }
    }, [navigate, isRadiologist]);


    const loadAllocation = (patient?.patientStateId ?? 0) >= Dtos.PatientStateType.Allocation && (patient?.patientStateId ?? 0) <= Dtos.PatientStateType.ScreenFail;
    const { data: patientAllocationFactors, error: patientAllocationFactorsError } = usePatientAllocationFactors(loadAllocation ? patient?.id : undefined);

    const [markAsIneligibleDialogOpen, setMarkAsIneligibleDialogOpen] = useState<boolean>(false);

    const markAsIneligibleLookups: Dtos.ILookup[] = useMemo(() => {
        var ineligibleReasonLookup = {
            propertyName: 'ineligibleReason',
            items: [] as Dtos.ILookupItem[]
        };

        ineligibleReasonLookup.items = INELIGIBILITY_REASONS.map((x, index) => {
            return {
                id: index,
                value: x
            } as Dtos.ILookupItem
        });

        var ineligibleBreastCancerSubtypeLookup = {
            propertyName: 'ineligibleBreastCancerSubtype',
            items: [] as Dtos.ILookupItem[]
        };

        ineligibleBreastCancerSubtypeLookup.items = INELIGIBLE_BREAST_CANCER_SUBTYPE.map((x, index) => {
            return {
                id: index,
                value: x
            } as Dtos.ILookupItem
        });

        return [ineligibleReasonLookup, ineligibleBreastCancerSubtypeLookup];
    }, []);

    const onMarkAsIneligibleSubmit: IFormSubmit<IMarkAsIneligibleForm, IFormValidationError> = useCallback(async (form) => {
        setPatientStatusSaving(true);

        let refreshPatient = false;

        try {
            const notes = `
                ${form.value?.ineligibleReason != null ? INELIGIBILITY_REASONS[form.value.ineligibleReason] : ""}
                ${
                    form.value?.ineligibleReasonOtherSpecify != null ? 
                        ' - ' + form.value.ineligibleReasonOtherSpecify : 
                    form.value?.ineligibleBreastCancerSubtype != null ? 
                        ' - ' + INELIGIBLE_BREAST_CANCER_SUBTYPE[form.value?.ineligibleBreastCancerSubtype] : 
                        ''
                }
            `;

            await serviceStackClient.post(new Dtos.SetPatientStatus({
                patientId: patient?.id,
                patientStateId: Dtos.PatientStateType.Ineligible,
                notes: notes
            }));

            refreshPatient = true;

            setPatientStatusSaving(false);

            setMarkAsIneligibleDialogOpen(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                    Participant Marked As Ineligible
                    </AlertTitle>
                    Participant status was successfully marked as ineligible.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setPatientStatusSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Marked As Ineligible
                    </AlertTitle>
                    {error.responseStatus?.errorCode === 'ClientViewableException' ? error.responseStatus.message : 'An error occurred while attempting to mark the participant as ineligible.'}
                </>,
                { variant: 'critical' }
            );
        }
    

        if (refreshPatient) {
            await patientRefresh();

            if (managerRef.current) {
                await managerRef.current.load();
            }
        }
    
    }, [serviceStackClient, enqueueSnackbar, setMarkAsIneligibleDialogOpen, setPatientStatusSaving, patientRefresh, managerRef, patient]);

    const markAsIneligiblelValidate: IFormValidate<IMarkAsIneligibleForm, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.ineligibleReason === undefined) {
            errors['ineligibleReason'] = [
                {
                    property: 'ineligibleReason',
                    code: 'I-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Critical
                }
            ];
        }

        if (formState.value?.ineligibleReason === Dtos.LookupIneligibleReasonType.IneligibleBreastCancerSubType && formState.value?.ineligibleBreastCancerSubtype === undefined) {
            errors['ineligibleBreastCancerSubtype'] = [
                {
                    property: 'ineligibleBreastCancerSubtype',
                    code: 'I-002',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Critical
                }
            ];
        }

        if(formState.value?.ineligibleReason === Dtos.LookupIneligibleReasonType.OtherSpecify && (formState.value?.ineligibleReasonOtherSpecify === undefined || formState.value.ineligibleReasonOtherSpecify.length == 0)) {
            errors['ineligibleReasonOtherSpecify'] = [
                {
                    property: 'ineligibleReasonOtherSpecify',
                    code: 'I-003',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: Dtos.ValidationErrorType.Critical
                }
            ];
        }

        return errors;
    }, [institution]);

    useEffect(() => {
        if (patientAllocationFactorsError) {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Patient Allocation Factors Not Loaded
                    </AlertTitle>
                    {patientAllocationFactorsError.errorCode === 'ClientViewableException' ? patientAllocationFactorsError.message : 'An error occurred while attempting to retrieve the patient allocation factors.'}
                </>,
                { variant: 'critical' }
            );
        }
    }, [patientAllocationFactorsError])

    const patientAllocationIneligible = patientAllocationFactors?.er === Dtos.LookupPositiveNegativeType.Negative && patientAllocationFactors?.her2 === Dtos.LookupPositiveNegativeType.Negative;

    const onSystemErrorConfirm = useCallback(() => {
        setSystemErrorDialogOpen(false);

        navigate(`/registration/${institution?.code}/${patient?.studyNumber}/staff/1/system-error-form/1/override`);
    }, [setSystemErrorDialogOpen, patient, institution, history])

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<PatientBreadcrumbs />}
            heading='Participant Summary'
        >
            <StyledContainer>
                <PatientInformation
                    patientCaption={patientCaption}
                    patientStateText={patientStateName}
                    patientStateCaption={patientStateCaption}
                />

                <StyledTitle>
                    <Box>
                        {
                            (patient?.patientStateId ?? 0) >= Dtos.PatientStateType.Allocated && (patient?.patientStateId ?? 0) < Dtos.PatientStateType.ScreenFail && (
                                <StyledButton
                                    variant="contained"
                                    color="primary"
                                    disabled={!(patient as Dtos.Patient)?.registrationNumber}
                                    //@ts-expect-error
                                    component="a"
                                    href={formsDueScheduleReportUrl}
                                >
                                    Forms Due Schedule
                                </StyledButton>
                            )
                        }
                    </Box>
                    <Box>
                        <PatientActiveDirectory 
                            canImpersonatePatient={canImpersonatePatient}
                            canManagePatientAccount={canManagePatientAccount}
                        />
                    </Box>
                </StyledTitle>

                <PatientSummaryAllocationInformation 
                    canUpdatePatient={canUpdatePatient}
                    isRadiologist={isRadiologist}
                    patient={patient}
                    patientAllocationIneligible={patientAllocationIneligible}
                    treatment={treatment}
                />

                <PatientStatusInformation 
                    patient={patient}
                />

                <Box
                    sx={{
                        mt: 2
                    }}
                >
                    <PatientSummary 
                        institutionCode={institution?.code}
                        eventActions={eventActionMapping}
                        subscribe={{
                            onUpdate: onPatientValidChange,
                            subscription: {
                                valid: true,
                                data: true
                            }
                        }}
                        managerRef={managerRef}
                    />
                </Box>
                
                <PatientSummaryAlerts 
                    patientState={patient?.patientStateId}
                    patientValid={patientValid}
                    patientAllocationIneligible={patientAllocationIneligible}
                />

                <StyledButtonGroup>   
                    {
                        patient?.patientStateId === Dtos.PatientStateType.Ineligible && canAdministerOpms && (
                            <Button
                                loading={saving}
                                variant="contained"
                                color="primary"
                                onClick={onRestoreClick}
                            >
                                Restore Patient
                            </Button>
                        )
                    } 
                    {
                        (patient?.patientStateId === Dtos.PatientStateType.ConsentPending || patient?.patientStateId === Dtos.PatientStateType.Consent)  && canUpdatePatient && (
                            <>
                                <Button
                                    loading={saving}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setScreenFailDialogOpen(true)}
                                >
                                    Mark As Screen Fail
                                </Button>
                                <Dialog
                                    open={screenFailDialogOpen}
                                    onClose={() => setScreenFailDialogOpen(false)}
                                    aria-labelledby="screen-fail-dialog-title"
                                    aria-describedby="screen-fail-dialog-description"
                                    fullWidth
                                >
                                    <StyledDialogTitle
                                        color="secondary"
                                    >
                                        Screening Failed (Participant {patient?.studyNumber})
                                    </StyledDialogTitle>
                                    <Form
                                        initialValue={undefined}
                                        onSubmit={onScreenFailSubmit}    
                                        lookups={screeningFormLookups}
                                        labels={undefined}
                                        validateOn={ValidateOn.onChange}  
                                        onValidate={screenFailValidate}
                                        debug={AppSettings.debug}              
                                    >
                                        <GroupedField
                                            propertyPath="screenFailReason"
                                            component={Select}
                                            LabelProps={{
                                                label: "Please provide the reason for failing the screening" as any
                                            }}
                                            FieldGridProps={{
                                                size: {
                                                    xs: 12,
                                                    sm: 12,
                                                    md: 12,
                                                    lg: 12,
                                                    xl: 12
                                                }
                                            }}
                                        />

                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            style={{margin: '8px'}}
                                        >
                                            <Button
                                                color="secondary"
                                                onClick={() => setScreenFailDialogOpen(false)}
                                                loading={screenFailSaving}  
                                            >
                                                Cancel
                                            </Button>

                                            <FormSubmitButton
                                                color="primary"   
                                                variant="contained"     
                                                loading={screenFailSaving}                                    
                                            >
                                                Screen Fail
                                            </FormSubmitButton>

                                        </Box>
                                    </Form>
                                </Dialog>
                            </>
                        )
                    }
                    {
                        (patient?.patientStateId === Dtos.PatientStateType.Consent) && canUpdatePatient && (
                            <Button
                                loading={saving}
                                variant="contained"
                                color="primary"
                                onClick={onCompleteConsentClick}
                                disabled={patientCritical || (!patientValid && !canBypassErrors)}
                            >
                                Complete Consent
                            </Button>
                        )
                    }
                    {
                        (patient?.patientStateId ?? 0) >= Dtos.PatientStateType.Registration && (patient?.patientStateId ?? 0) < Dtos.PatientStateType.Allocated && canUpdatePatient && (
                            <>
                                <Button
                                    loading={saving}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setMarkAsIneligibleDialogOpen(true)}
                                >
                                    Mark As Ineligible
                                </Button>
                                <Dialog
                                    open={markAsIneligibleDialogOpen}
                                    onClose={() => setMarkAsIneligibleDialogOpen(false)}
                                    aria-labelledby="ineligible-dialog-title"
                                    aria-describedby="ineligible-dialog-description"
                                    maxWidth="sm"
                                    fullWidth
                                >
                                    <StyledDialogTitle
                                        color="secondary"
                                    >
                                        Mark As Ineligible
                                    </StyledDialogTitle>

                                    <Form
                                        initialValue={undefined}
                                        onSubmit={onMarkAsIneligibleSubmit}    
                                        lookups={markAsIneligibleLookups}
                                        labels={undefined}
                                        validateOn={ValidateOn.onChange} 
                                        onValidate={markAsIneligiblelValidate}              
                                    >
                                        <GroupedFields>
                                            <GroupedField 
                                                propertyPath="ineligibleReason"
                                                LabelProps={{
                                                    label: "Ineligible Reason" as any
                                                }}
                                                component={Select}
                                                FieldGridProps={{
                                                    size: {
                                                        xl: 12,
                                                        lg: 12,
                                                        md: 12,
                                                        sm: 12,
                                                        xs: 12
                                                    }
                                                }}
                                            />

                                            <CrfCondition
                                                mode={ConditionMode.Show}
                                                condition={ineligibleBreastCancerSubType}
                                                subscription={{ value: true }}
                                            >
                                                <GroupedField 
                                                    propertyPath="ineligibleBreastCancerSubtype"
                                                    LabelProps={{
                                                        label: "Ineligible Breast Cancer Subtype" as any
                                                    }}
                                                    component={Select}
                                                    FieldGridProps={{
                                                        size: {
                                                            xl: 12,
                                                            lg: 12,
                                                            md: 12,
                                                            sm: 12,
                                                            xs: 12
                                                        }
                                                    }}
                                                />  
                                            </CrfCondition>  

                                            <CrfCondition
                                                mode={ConditionMode.Show}
                                                condition={ineligibleReasonOtherSpecify}
                                                subscription={{ value: true }}
                                            >
                                                <GroupedField 
                                                    propertyPath="ineligibleReasonOtherSpecify"
                                                    LabelProps={{
                                                        label: "Specify" as any
                                                    }}
                                                    component={Text}
                                                    rows={3}
                                                    FieldGridProps={{
                                                        size: {
                                                            xl: 12,
                                                            lg: 12,
                                                            md: 12,
                                                            sm: 12,
                                                            xs: 12
                                                        }
                                                    }}
                                                />  
                                            </CrfCondition>  
                                        </GroupedFields>

                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            gap={2}
                                            padding={2}
                                        >
                                            <Button
                                                color="secondary"
                                                onClick={() => setMarkAsIneligibleDialogOpen(false)}
                                                loading={patientStatusSaving}  
                                            >
                                                Cancel
                                            </Button>

                                            <FormSubmitButton
                                                color="primary"   
                                                variant="contained"     
                                                loading={patientStatusSaving}                                    
                                            >
                                                Submit
                                            </FormSubmitButton>
                                        </Box>
                                    </Form>
                                </Dialog>
                            </>
                        )
                    }
                    {
                        patient?.patientStateId === Dtos.PatientStateType.Registration && canUpdatePatient && (
                            <>
                                <Button
                                    loading={saving}
                                    variant="contained"
                                    color="primary"
                                    onClick={onRegisterClick}
                                    disabled={patientCritical || (!patientValid && !canBypassErrors)}
                                >
                                    Register
                                </Button>
                                <Dialog
                                    open={reviewStartDialogOpen}
                                    onClose={() => setReviewStartDialogOpen(false)}
                                    aria-labelledby="review-dialog-title"
                                    aria-describedby="review-dialog-description"
                                    maxWidth="sm"
                                    fullWidth
                                >
                                    <StyledDialogTitle
                                        color="secondary"
                                    >
                                        Register
                                    </StyledDialogTitle>

                                    <DialogContent 
                                        style={{ paddingTop: 0 }}
                                    >
                                        <DialogContentText>
                                            Radiologist review process will start when the patient is registered.
                                        </DialogContentText>

                                        <DialogContentText >
                                            Please select a radiologist. The selected radiologist will receive an email notification about the review.
                                        </DialogContentText>
                                    </DialogContent>

                                    <Form
                                        initialValue={undefined}
                                        onSubmit={onReviewStartSubmit}    
                                        lookups={undefined}
                                        labels={undefined}
                                        validateOn={ValidateOn.onChange}  
                                        onValidate={reviewStartValidate}
                                        debug={AppSettings.debug}
                                    >
                                        <RadiologistSelector 
                                            dialog={true}
                                        />

                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            gap={2}
                                            padding={2}
                                        >
                                            <Button
                                                color="secondary"
                                                onClick={() => setReviewStartDialogOpen(false)}
                                                loading={reviewStartSaving}  
                                            >
                                                Cancel
                                            </Button>

                                            <FormSubmitButton
                                                color="primary"   
                                                variant="contained"     
                                                loading={reviewStartSaving}                                    
                                            >
                                                Register
                                            </FormSubmitButton>

                                        </Box>
                                    </Form>
                                </Dialog>
                            </>
                        )
                    }
                    {
                        patient?.patientStateId === Dtos.PatientStateType.RadiologistReview && canAdministerOpms && (
                            <>
                                <Button
                                    loading={saving}
                                    variant="contained"
                                    color="primary"
                                    onClick={onCompleteRadiologyReviewClick}
                                    disabled={patientCritical || patientAllocationIneligible || (!patientValid && !canBypassErrors)}
                                >
                                    Complete Radiology Review
                                </Button>
                            </>
                        )
                    }  
                    {
                        (patient?.patientStateId === Dtos.PatientStateType.Allocation || patient?.patientStateId === Dtos.PatientStateType.CorrectionRequired) && canUpdatePatient && (
                            <>
                                <Button
                                    loading={saving}
                                    variant="contained"
                                    color="primary"
                                    onClick={onAllocateClick}
                                    disabled={patientCritical || patientAllocationIneligible || (!patientValid && !canBypassErrors)}
                                >
                                    Allocate
                                </Button>
                            </>
                        )
                    }  
                    {
                        (patient?.patientStateId === Dtos.PatientStateType.BctReview) && canAdministerOpms && (
                            <>
                                <Button
                                    loading={patientStatusSaving || saving}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setCorrectionRequiredDialogOpen(true)}
                                >
                                    Correction Required
                                </Button>
                                <Dialog
                                    open={correctionRequiredDialogOpen}
                                    onClose={() => setCorrectionRequiredDialogOpen(false)}
                                    aria-labelledby="correction-dialog-title"
                                    aria-describedby="correction-dialog-description"
                                    maxWidth="sm"
                                    fullWidth
                                >
                                    <StyledDialogTitle
                                        color="secondary"
                                    >
                                        Correction Required
                                    </StyledDialogTitle>

                                    <DialogContent 
                                        style={{ paddingTop: 0 }}
                                    >
                                        <DialogContentText >
                                            Please provide data correction details.
                                        </DialogContentText>
                                    </DialogContent>

                                    <Form
                                        initialValue={correctionRequiredInitial}
                                        onSubmit={onPatientStatusSubmit}    
                                        lookups={undefined}
                                        labels={undefined}
                                        validateOn={ValidateOn.onChange} 
                                        onValidate={patientStatusValidate}              
                                    >
                                        <GroupedField 
                                            propertyPath="notes"
                                            LabelProps={{
                                                label: "Reason" as any
                                            }}
                                            component={Text}
                                            FieldGridProps={{
                                                size: {
                                                    xl: 12,
                                                    lg: 12,
                                                    md: 12,
                                                    sm: 12,
                                                    xs: 12
                                                }
                                            }}
                                        />

                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            gap={2}
                                            padding={2}
                                        >
                                            <Button
                                                color="secondary"
                                                onClick={() => setCorrectionRequiredDialogOpen(false)}
                                                loading={patientStatusSaving}  
                                            >
                                                Cancel
                                            </Button>

                                            <FormSubmitButton
                                                color="primary"   
                                                variant="contained"     
                                                loading={patientStatusSaving}                                    
                                            >
                                                Submit
                                            </FormSubmitButton>
                                        </Box>
                                    </Form>
                                </Dialog>
                            </>
                        )
                    }                
                    {
                        patient?.patientStateId === Dtos.PatientStateType.BctReview && canAdministerOpms && (
                            <Button
                                loading={patientStatusSaving || saving}
                                variant="contained"
                                color="primary"
                                onClick={onAllocateClick}
                                disabled={patientCritical || patientAllocationIneligible || (!patientValid && !canBypassErrors)}
                            >
                                Approve Allocation
                            </Button>
                        )
                    }
                </StyledButtonGroup>

                <Dialog
                    open={systemErrorDialogOpen}
                    onClose={() => setSystemErrorDialogOpen(false)}
                    aria-labelledby="system-error-dialog-title"
                    aria-describedby="system-error-dialog-description"
                    maxWidth="sm"
                    fullWidth
                >
                    <StyledDialogTitle
                        color="secondary"
                    >
                        System Error Override
                    </StyledDialogTitle>
                    <DialogContent 
                        style={{ paddingTop: 0 }}
                    >
                        <DialogContentText>
                            <Typography variant='body1'>The patient has outstanding errors &mdash; Is this a system error?</Typography>
                        </DialogContentText>

                        <DialogContentText >
                            <Typography>Do you want to proceed?</Typography>
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setSystemErrorDialogOpen(false)} color="secondary">
                            No
                        </Button>
                        <Button onClick={onSystemErrorConfirm} color="primary" variant="contained" autoFocus>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            </StyledContainer>
        </PageLayout>
    );
}

export default PatientSummaryPage